import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <title>漫画未找到 - 韩国漫画网</title>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-1 pt-20 flex items-center justify-center">
            <div className="text-center">
              <i className="ri-file-unknow-line text-6xl text-foreground-300 w-14 h-14 flex items-center justify-center mx-auto"></i>
              <h1 className="mt-4 text-xl font-bold text-foreground-900">漫画未找到</h1>
              <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedComics = comics
    .filter(c => c.category === comic.category && c.id !== comic.id)
    .slice(0, 5);

  const categoryInfo = categories.find(c => c.slug === comic.category);
  const viewsText = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();

  const seasonChapters = [
    { season: 1, range: '1-50话', desc: '初入宫廷，生死一线的求生之路' },
    { season: 2, range: '51-100话', desc: '权谋暗涌，父女情深的温暖羁绊' },
    { season: 3, range: '101-125话', desc: '真相浮现，命运交织的最终抉择' },
  ];

  const chapterList = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(Date.now() - i * 86400000).toISOString().split('T')[0],
  }));

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${comic.title} - ${comic.author}`,
    "description": comic.description,
    "image": comic.cover,
    "author": {
      "@type": "Person",
      "name": comic.author
    },
    "datePublished": comic.updateDate,
    "dateModified": comic.updateDate,
    "publisher": {
      "@type": "Organization",
      "name": "韩国漫画网",
      "url": "https://www.koreanmanhwa.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.koreanmanhwa.com/comic/${comic.id}`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://www.koreanmanhwa.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": categoryInfo?.name || "漫画分类",
        "item": `https://www.koreanmanhwa.com/category/${comic.category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": comic.title
      }
    ]
  };

  return (
    <>
      <title>{`${comic.title} - ${comic.author} | 无删减韩漫免费阅读 - 韩国漫画网`}</title>
      <meta name="description" content={`在线免费阅读${comic.title}，作者${comic.author}。${comic.description?.slice(0, 120)}... 韩国漫画网提供无删减下拉式韩漫在线阅读。`} />
      <meta name="keywords" content={`${comic.title},${comic.author},韩国漫画,韩漫在线,无删减免费阅读,${comic.tags?.join(',')}`} />
      <link rel="canonical" href={`https://www.koreanmanhwa.com/comic/${comic.id}`} />
      <meta property="og:title" content={`${comic.title} - ${comic.author} | 韩国漫画网`} />
      <meta property="og:description" content={`在线免费阅读${comic.title}，作者${comic.author}。${comic.description?.slice(0, 120)}...`} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://www.koreanmanhwa.com/comic/${comic.id}`} />
      <meta property="og:image" content={comic.cover} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${comic.title} - ${comic.author} | 韩国漫画网`} />
      <meta name="twitter:description" content={`在线免费阅读${comic.title}，作者${comic.author}。`} />
      <meta name="twitter:image" content={comic.cover} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || '漫画分类', href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />
            <article className="mt-6 bg-white rounded-lg border border-background-200/70 overflow-hidden">
              <div className="flex flex-col md:flex-row gap-6 p-5 md:p-8">
                <div className="flex-shrink-0 w-full md:w-56">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - ${comic.author} 韩国漫画封面`}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full md:w-56 aspect-[3/4] object-cover rounded-lg"
                  />
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                      {comic.title}
                    </h1>
                    <div className="flex items-center flex-wrap gap-3 mt-3">
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                        {comic.rating} 分
                      </span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${comic.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'}`}>
                        {comic.status}
                      </span>
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                        {comic.chapters}话
                      </span>
                      <span className="text-sm text-foreground-500 flex items-center gap-1">
                        <i className="ri-eye-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        {viewsText}
                      </span>
                    </div>
                    <div className="mt-4 flex items-center flex-wrap gap-2">
                      <span className="text-sm text-foreground-500">作者：</span>
                      <span className="text-sm font-medium text-foreground-800">{comic.author}</span>
                      <span className="text-foreground-300 mx-1">|</span>
                      <span className="text-sm text-foreground-500">分类：</span>
                      <Link to={`/category/${comic.category}`} className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer">
                        {categoryInfo?.name}
                      </Link>
                      <span className="text-foreground-300 mx-1">|</span>
                      <span className="text-sm text-foreground-500">更新：</span>
                      <span className="text-sm text-foreground-800">{comic.updateDate}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {comic.tags?.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-50 text-secondary-700 border border-secondary-200/50 whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5">
                      <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {comic.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-6">
                    <button className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-line text-base w-4 h-4 flex items-center justify-center"></i>
                      从第一话开始阅读
                    </button>
                    <button className="px-6 py-2.5 bg-secondary-100 text-secondary-700 text-sm font-semibold rounded-full hover:bg-secondary-200 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer">
                      <i className="ri-bookmark-line text-base w-4 h-4 flex items-center justify-center"></i>
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>
            </article>
            <section className="mt-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-list-check w-5 h-5 flex items-center justify-center text-primary-500"></i>
                    章节列表
                  </h2>
                  <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
                    {chapterList.map((ch) => (
                      <a
                        key={ch.num}
                        href={`/comic/${comic.id}/chapter/${ch.num}`}
                        className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                        <span className="text-xs text-foreground-400">{ch.date}</span>
                      </a>
                    ))}
                    {comic.chapters > 20 && (
                      <div className="text-center py-3">
                        <button className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
                          查看更多章节 ({comic.chapters - 20}话) <i className="ri-arrow-down-s-line w-4 h-4 inline-flex items-center justify-center"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:w-64 flex-shrink-0">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-stack-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                    季度信息
                  </h2>
                  <div className="space-y-3">
                    {seasonChapters.map((season) => (
                      <div key={season.season} className="bg-white rounded-lg border border-background-200/70 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-accent-100 text-accent-700 text-xs font-bold flex items-center justify-center whitespace-nowrap">
                            {season.season}
                          </span>
                          <span className="text-sm font-semibold text-foreground-900">第{season.season}季</span>
                        </div>
                        <p className="text-xs text-foreground-500 mb-2">{season.range}</p>
                        <p className="text-xs text-foreground-600 leading-relaxed">{season.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
            {relatedComics.length > 0 && (
              <section className="mt-10">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} {...c} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}